import { Column, Entity, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';
import { SalesRouteEntity } from './sales-route.entity';

/**
 * A client placed on a sales route, carrying the per-client visit cadence and
 * the lightweight day-scoped "skip" state that drives the daily due-list. The
 * sales-side analogue of `RouteClientEntity` (PD-13776).
 *
 * Whether the client was actually *visited* is NOT stored here — that reuses
 * the existing client-visit infrastructure (`ClientVisitEntity` +
 * `client.lastContactDate` + a `client_activity` timeline row). The only new
 * behavioural state this feature introduces is `skipThroughDate` below.
 */
@Entity('sales_route_client')
export class SalesRouteClientEntity extends BaseEntity {
	@Column({ name: 'fk_sales_route__id', type: 'int', unsigned: true })
	public salesRouteId!: number;

	@Column({ name: 'fk_client__id', type: 'int', unsigned: true })
	public clientId!: number;

	/** Display / planning order of this client within the route. */
	@Column({ name: 'sequence', type: 'int' })
	public sequence!: number;

	@Column({ name: 'notes', type: 'text', nullable: true })
	public notes?: string;

	/**
	 * The client's visit cadence on THIS route — a structurally-generated
	 * SUBSET of the route's own recurrence (`sales_route.recurrenceRule`), i.e.
	 * a "compatible child" RRULE. Identical semantics to the delivery side
	 * (`RouteClientEntity.recurrenceRule`):
	 *   NULL = inherit = due every time the route is due (the default);
	 *   a non-NULL value can only NARROW the route's schedule (a subset of its
	 *   days and/or a reduced frequency anchored to the route's occurrences) —
	 *   it can never add a day or run more often than the route.
	 * Compatibility is evaluated on read via the shared `libs/core`
	 * child-schedule utilities; there is no stored compatibility flag and an
	 * incompatible value falls back to inherit so a client is never silently
	 * dropped.
	 */
	@Column({
		name: 'recurrenceRule',
		type: 'varchar',
		length: 500,
		nullable: true,
	})
	public recurrenceRule?: string | null;

	/**
	 * Day-scoped "skip / snooze" control — the ONLY new behavioural state this
	 * feature introduces. The daily due-list hides this client while
	 * `today <= skipThroughDate`:
	 *   - "Skip just for today" → set to today; the client reappears on its
	 *     next firing occurrence (any later occurrence date is necessarily
	 *     `> skipThroughDate`).
	 *   - "Snooze until <date>" → set to that future date.
	 *   - "Un-skip"             → set back to NULL.
	 *
	 * Deliberately a single, overwritten date with NO history. A skip is
	 * ephemeral list-management, not a client touchpoint, so it is NOT logged
	 * to `client_activity` (an actual visit is, via the existing visit flow).
	 * Storing only the current value — never reconstructing past occurrences —
	 * is exactly what makes "on the next day we don't care about the past"
	 * automatic: there is no missed-stop state to surface or suppress.
	 */
	@Column({ name: 'skipThroughDate', type: 'date', nullable: true })
	public skipThroughDate?: Date | null;

	@ManyToOne(() => SalesRouteEntity, (route) => route.salesRouteClients)
	@JoinColumn({ name: 'fk_sales_route__id' })
	public fkSalesRoute!: SalesRouteEntity;

	// Optional in TS because the relation is only present when explicitly
	// loaded (and is null when the client has been soft-deleted).
	@ManyToOne(() => ClientEntity)
	@JoinColumn({ name: 'fk_client__id' })
	public fkClient?: ClientEntity;
}
